import { useState, useEffect, memo, useMemo } from 'react';
import debounce from 'bloko/common/debounce';
import { P as PostMessage } from '../postMessage-63ff07b9.js';
import { w as websocket } from '../WebsocketConnection-98a0364a.js';
import { _ as __assign, a as __awaiter, b as __generator } from '../_tslib-30326c5e.js';
import LocalStorage from 'bloko/common/storage/LocalStorageWrapper';

var invokeIfTabActive = function (func) {
    if (document.visibilityState === 'visible') {
        return func();
    }
    return undefined;
};

var shouldLog = false;
if (typeof window !== 'undefined') {
    shouldLog = location.search.includes('chatik-integration-log');
}
function log(message) {
    if (!shouldLog) {
        return;
    }
    var currentTime = new Date();
    var hours = String(currentTime.getHours()).padStart(2, '0');
    var minutes = String(currentTime.getMinutes()).padStart(2, '0');
    var seconds = String(currentTime.getSeconds()).padStart(2, '0');
    var formattedTime = "[".concat(hours, ":").concat(minutes, ":").concat(seconds, "]:");
    // eslint-disable-next-line no-console
    console.log("".concat(formattedTime, " ").concat(message));
}

/**
 * Планировщик обновлений с защитой от параллельных запусков
 * Возвращает объект с методами stop и schedule
 */
var createScheduler = function () {
    var timeoutId;
    var stop = function () {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    };
    var schedule = function (action, delay) {
        stop();
        timeoutId = setTimeout(action, delay);
    };
    return {
        stop: stop,
        schedule: schedule,
    };
};

var COUNTER_UPDATER_LS_KEY = '__chatikIntegration_counterUpdater';
/** Задержка для обновления значения в основном потоке */
var UPDATE_DELAY_WITHOUT_SOCKET_CONNECTION = 2 * 60 * 1000;
/** Задержка для обновления значения при подключенном вебсокет соединении "на всякий случай" */
var UPDATE_DELAY_WITH_SOCKET_CONNECTION = 10 * 60 * 1000;
/** Если значение счетчика не старше этого врмени, то считаем, что данные "свежие" и их можно использовать */
var EXPIRATION_TIME = 10 * 60 * 1000;
var UPDATE_COUNT_DEBOUNCE_DELAY = 1000;

var useIsTabActive = function () {
    var _a = useState(typeof document === 'undefined' ? 'hidden' : document.visibilityState), visibilityState = _a[0], setVisibilityState = _a[1];
    useEffect(function () {
        var handleVisibilityChange = function () {
            setVisibilityState(document.visibilityState);
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return function () {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);
    return visibilityState === 'visible';
};

var DEFAULT_VALUE = {
    counterId: null,
    unreadCount: null,
    unreadSupportCount: null,
    lastUpdateAt: 0,
};
var getStorageData = function () {
    var value = LocalStorage.getItem(COUNTER_UPDATER_LS_KEY);
    if (!value) {
        return DEFAULT_VALUE;
    }
    try {
        return JSON.parse(value);
    }
    catch (_) {
        return DEFAULT_VALUE;
    }
};

var getBaseUpdateDelay = function () {
    return websocket.connected ? UPDATE_DELAY_WITH_SOCKET_CONNECTION : UPDATE_DELAY_WITHOUT_SOCKET_CONNECTION;
};
var getUpdateDelay = function () {
    var value = getStorageData();
    var timeSinceLastUpdate = Date.now() - value.lastUpdateAt;
    var baseDelay = getBaseUpdateDelay();
    log("base delay ".concat(baseDelay, ", websocket connected ").concat(String(websocket.connected)));
    var delay = 0;
    if (timeSinceLastUpdate > baseDelay) {
        delay = 0;
    }
    delay = Math.max(baseDelay - timeSinceLastUpdate, 0);
    log("timeSinceLastUpdate - ".concat(timeSinceLastUpdate, ", delay before update - ").concat(delay));
    return delay;
};
/**
 * При рестарте nginx (например при релизе) все коннекты рвутся и одновременно пытаются переподключится.
 * Это создает избыточную нагрузку. Чтобы распределить нагрузку используется нестатичный таймаут.
 */
var getRandomizedUpdateDelay = function () {
    return (Math.random() + 0.25) * UPDATE_DELAY_WITHOUT_SOCKET_CONNECTION;
};

var writeToStorage = function (counters) {
    var counterId = getStorageData().counterId;
    LocalStorage.setItem(COUNTER_UPDATER_LS_KEY, JSON.stringify(__assign(__assign({}, counters), { counterId: counterId, lastUpdateAt: Date.now() })));
};
var resetLastUpdateTime = function () {
    var data = getStorageData();
    LocalStorage.setItem(COUNTER_UPDATER_LS_KEY, JSON.stringify(__assign(__assign({}, data), { lastUpdateAt: 0 })));
};
var resetStorageCountersWithNewId = function (counterId) {
    LocalStorage.setItem(COUNTER_UPDATER_LS_KEY, JSON.stringify(__assign(__assign({}, DEFAULT_VALUE), { counterId: counterId })));
};

var getInitialValues = function () {
    var _a = getStorageData(), lastUpdateAt = _a.lastUpdateAt, unreadCount = _a.unreadCount, unreadSupportCount = _a.unreadSupportCount;
    var timeSinceLastUpdate = Date.now() - lastUpdateAt;
    return timeSinceLastUpdate < EXPIRATION_TIME ? { unreadCount: unreadCount, unreadSupportCount: unreadSupportCount } : null;
};

var fetchCounters = function () { return __awaiter(void 0, void 0, void 0, function () {
    var resp, countersValue;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                log('fetching counter..');
                return [4 /*yield*/, fetch("https://".concat(location.host, "/chatik/api/unread"), {
                        headers: { Accept: 'application/json' },
                    })];
            case 1:
                resp = _a.sent();
                return [4 /*yield*/, resp.json()];
            case 2:
                countersValue = (_a.sent());
                log("recieved counters value: ".concat(JSON.stringify(countersValue || {})));
                return [2 /*return*/, countersValue];
        }
    });
}); };

var runRecursiveCounterUpdate = function (_a) { return __awaiter(void 0, [_a], void 0, function (_b) {
    var counters;
    var onUpdate = _b.onUpdate, scheduler = _b.scheduler;
    return __generator(this, function (_d) {
        switch (_d.label) {
            case 0:
                _d.trys.push([0, 2, , 3]);
                return [4 /*yield*/, fetchCounters()];
            case 1:
                counters = _d.sent();
                return [3 /*break*/, 3];
            case 2:
                _d.sent();
                log('fetch counter error');
                scheduler.schedule(function () { return runRecursiveCounterUpdate({ onUpdate: onUpdate, scheduler: scheduler }); }, getRandomizedUpdateDelay());
                return [2 /*return*/];
            case 3:
                onUpdate(__assign(__assign({}, counters), { fromRequest: true }));
                writeToStorage(counters);
                scheduler.schedule(function () { return runRecursiveCounterUpdate({ onUpdate: onUpdate, scheduler: scheduler }); }, getBaseUpdateDelay());
                return [2 /*return*/];
        }
    });
}); };

var useStorageEvent = function (onUpdate, counterId) {
    useEffect(function () {
        // При изменении counterId сбрасываем значение счетчиков и время последнего обновления для того, чтобы сразу обновить данные
        // Этот хук должен идти до хука с получением initialValues, так как в initialValues необходимо иметь уже проверенный localStorage
        if (getStorageData().counterId !== counterId) {
            log('counter id changed - reset storage counters');
            resetStorageCountersWithNewId(counterId);
        }
    }, [counterId]);
    useEffect(function () {
        var initialValues = getInitialValues();
        if (initialValues) {
            onUpdate(__assign(__assign({}, initialValues), { fromRequest: false }));
        }
        var onStorageEvent = function () {
            var value = getStorageData();
            log('storage event');
            /**
             * Тут нужна проверка на то, что значение изменилось, что бы не дергать лишний раз коллбек
             * Для этого нужно хранить состояние счетчика в стейте компонента,
             * а onUpdate дергать только в эффекте когда значение меняется
             */
            onUpdate(__assign(__assign({}, value), { fromRequest: false }));
        };
        window.addEventListener('storage', onStorageEvent);
        return function () { return window.removeEventListener('storage', onStorageEvent); };
    }, [onUpdate]);
};

var scheduler = createScheduler();
// eslint-disable-next-line react/display-name
var CounterUpdater = memo(function (_a) {
    var websocketOrigin = _a.websocketOrigin, chatikOrigin = _a.chatikOrigin, counterId = _a.counterId, onUpdate = _a.onUpdate;
    var isTabActive = useIsTabActive();
    var runRecursiveCounterUpdate$1 = useMemo(function () { return debounce(function () { return runRecursiveCounterUpdate({ onUpdate: onUpdate, scheduler: scheduler }); }, UPDATE_COUNT_DEBOUNCE_DELAY); }, [onUpdate]);
    useStorageEvent(onUpdate, counterId);
    useEffect(function () {
        if (isTabActive) {
            log('start scheduler');
            scheduler.schedule(runRecursiveCounterUpdate$1, getUpdateDelay());
        }
        else {
            log('stop scheduler');
            scheduler.stop();
        }
    }, [isTabActive, runRecursiveCounterUpdate$1]);
    useEffect(function () {
        websocket.connect({
            origin: websocketOrigin,
            handlers: {
                connect: function () {
                    return invokeIfTabActive(function () {
                        log('ws connected');
                        // при подключении перезапускаем обновление счетика с увеличенной задержкой
                        scheduler.schedule(runRecursiveCounterUpdate$1, getUpdateDelay());
                    });
                },
                disconnect: function () {
                    return invokeIfTabActive(function () {
                        log('ws disconnected');
                        // при дисконекте используем рандомизированную задержку, потому что
                        // дисконнект может быть массовый из-за перезагрузки nginx
                        scheduler.schedule(runRecursiveCounterUpdate$1, getRandomizedUpdateDelay());
                    });
                },
                unread_chats_count_change: function () {
                    log("received ws event - unread_chats_count_change");
                    // при получении сообщения об обновлении счетчика,
                    // если вкладка активна - запрашиваем сразу
                    // если нет - сбрасываем время последнего обновления,
                    // что бы запросить сразу, как вкладка станет активной
                    if (document.visibilityState === 'visible') {
                        scheduler.schedule(runRecursiveCounterUpdate$1, 0);
                    }
                    else {
                        resetLastUpdateTime();
                    }
                },
            },
        });
    }, [runRecursiveCounterUpdate$1, websocketOrigin]);
    useEffect(function () {
        // на случай если нет вебсокет подключения
        // чатик отправляет postMessage когда он считает,
        // что нужно перезапросить счетчик
        var removeListeners = PostMessage.on({
            origin: chatikOrigin,
            handlers: {
                updateCounter: function () {
                    log('received post message - updateCounter');
                    // если есть сокет, не нужно обрабатывать это событие,
                    // так как придет unread_chats_count_change
                    if (websocket.connected) {
                        return;
                    }
                    if (document.visibilityState === 'visible') {
                        scheduler.schedule(runRecursiveCounterUpdate$1, 0);
                    }
                    else {
                        resetLastUpdateTime();
                    }
                },
            },
        });
        return function () { return removeListeners(); };
    }, [runRecursiveCounterUpdate$1, chatikOrigin]);
    return null;
}, 
// не реагируем на изменение внешних пропсов,
// так как логика копонента предполагает только первоначальную инициализацию
function () { return true; });

export { CounterUpdater };
//# sourceMappingURL=index.js.map
